import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import AuthContext from '../contexts/AuthContext';
import {
  getActiveDashboardAnnouncements,
  recordAnnouncementView,
  dismissAnnouncement,
  acknowledgeAnnouncement
} from '../services/announcementService';
import './AnnouncementBanner.css';

const AnnouncementBanner = () => {
  const [announcement, setAnnouncement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const { user } = useContext(AuthContext);

  // Track if component is mounted to prevent setState on unmounted component
  const isMountedRef = useRef(true);

  // Track which announcement we've recorded a view for to prevent duplicates
  const viewRecordedRef = useRef(null);

  // Validate URL scheme to prevent XSS
  const isSafeUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    const trimmedUrl = url.trim().toLowerCase();
    return trimmedUrl.startsWith('http://') ||
           trimmedUrl.startsWith('https://') ||
           trimmedUrl.startsWith('/');
  };

  // Validate announcement data shape
  const isValidAnnouncement = (ann) => {
    return ann &&
           typeof ann === 'object' &&
           ann._id &&
           ann.title &&
           typeof ann.title === 'string' &&
           ann.content &&
           typeof ann.content === 'string' &&
           ann.type &&
           ['system', 'feature', 'motivation', 'urgent'].includes(ann.type);
  };

  const fetchAnnouncement = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getActiveDashboardAnnouncements();

      // Only update state if component is still mounted
      if (!isMountedRef.current) return;

      if (response.success && response.data.announcement) {
        const ann = response.data.announcement;

        // Validate announcement data
        if (!isValidAnnouncement(ann)) {
          console.error('Invalid announcement data:', ann);
          setError('Invalid announcement data');
          setLoading(false);
          return;
        }

        setAnnouncement(ann);

        // Record view automatically only if we haven't already recorded for this announcement
        if (viewRecordedRef.current !== ann._id) {
          try {
            await recordAnnouncementView(ann._id);
            viewRecordedRef.current = ann._id;
          } catch (viewError) {
            // Silently fail view recording - non-critical
            console.error('Failed to record view:', viewError);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching announcement:', err);
      if (isMountedRef.current) {
        setError('Failed to load announcement');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []); // No dependencies needed since we use refs for tracking

  useEffect(() => {
    isMountedRef.current = true;

    if (user) {
      fetchAnnouncement();
    } else {
      setLoading(false);
    }

    // Cleanup function
    return () => {
      isMountedRef.current = false;
    };
  }, [user, fetchAnnouncement]);

  const handleDismiss = async () => {
    if (!announcement || actionLoading) return;

    try {
      setActionLoading(true);
      await dismissAnnouncement(announcement._id);

      if (isMountedRef.current) {
        setDismissed(true);
      }
    } catch (err) {
      console.error('Error dismissing announcement:', err);
      if (isMountedRef.current) {
        setError('Failed to dismiss. Please try again.');
      }
    } finally {
      if (isMountedRef.current) {
        setActionLoading(false);
      }
    }
  };

  const handleAcknowledge = async () => {
    if (!announcement || actionLoading) return;

    try {
      setActionLoading(true);
      await acknowledgeAnnouncement(announcement._id);

      if (isMountedRef.current) {
        setDismissed(true);
      }
    } catch (err) {
      console.error('Error acknowledging announcement:', err);
      if (isMountedRef.current) {
        setError('Failed to acknowledge. Please try again.');
      }
    } finally {
      if (isMountedRef.current) {
        setActionLoading(false);
      }
    }
  };

  const handleRetry = () => {
    setError(null);
    fetchAnnouncement();
  };

  // Don't render if loading, no announcement, or dismissed
  if (loading || !announcement || dismissed) {
    return null;
  }

  // Show error state with retry option
  if (error) {
    return (
      <div className="announcement-banner announcement-error">
        <div className="announcement-content">
          <div className="announcement-icon">⚠️</div>
          <div className="announcement-text">
            <p className="announcement-message">{error}</p>
          </div>
        </div>
        <div className="announcement-actions">
          <button
            onClick={handleRetry}
            className="announcement-retry-btn"
            aria-label="Retry"
          >
            ↻
          </button>
        </div>
      </div>
    );
  }

  // Get banner class based on type
  const getBannerClass = () => {
    const baseClass = 'announcement-banner';
    switch (announcement.type) {
      case 'urgent':
        return `${baseClass} announcement-urgent`;
      case 'motivation':
        return `${baseClass} announcement-motivation`;
      case 'feature':
        return `${baseClass} announcement-feature`;
      default:
        return `${baseClass} announcement-system`;
    }
  };

  // Get icon based on type
  const getIcon = () => {
    switch (announcement.type) {
      case 'urgent':
        return '⚠️';
      case 'motivation':
        return '💚';
      case 'feature':
        return '📢';
      default:
        return 'ℹ️';
    }
  };

  return (
    <div className={getBannerClass()}>
      <div className="announcement-content">
        <div className="announcement-icon">{getIcon()}</div>
        <div className="announcement-text">
          <h4 className="announcement-title">{announcement.title}</h4>
          <p className="announcement-message">{announcement.content}</p>

          {announcement.actionButton &&
           announcement.actionButton.url &&
           isSafeUrl(announcement.actionButton.url) && (
            <a
              href={announcement.actionButton.url}
              target="_blank"
              rel="noopener noreferrer"
              className="announcement-cta"
            >
              {announcement.actionButton.text || 'Learn More'}
            </a>
          )}
        </div>
      </div>

      <div className="announcement-actions">
        {announcement.requireAcknowledgment ? (
          <button
            onClick={handleAcknowledge}
            className="announcement-acknowledge-btn"
            disabled={actionLoading}
          >
            {actionLoading ? 'Loading...' : 'I Understand'}
          </button>
        ) : (
          announcement.dismissible && (
            <button
              onClick={handleDismiss}
              className="announcement-dismiss-btn"
              aria-label="Dismiss announcement"
              disabled={actionLoading}
            >
              {actionLoading ? '...' : '✕'}
            </button>
          )
        )}
      </div>
    </div>
  );
};

export default AnnouncementBanner;
